import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';

/**
 * Section „Beitragsänderungs-Information“ of the billing area object (requirement 01 §6.2): the
 * `bm.PremiumChangeNoticeType` configurations of ONE billing area - which ns notification type sends the
 * premium change letter of the area's billing runs.
 *
 * Same pattern as {@code bm-billing-area-payment-reminder-types}: a metadata-driven QL CRUD table (generic
 * create/edit form, no manual form) with the area as fixed filter and as read-only FK default (CONFIG-012).
 * The import object context carries the area as well, so the backend `PremiumChangeNoticeTypeForm` pre-fills
 * area and status „aktiv“ for a new type.
 *
 * The widget alias is the configuration alias of the requirement, deliberately WITHOUT the area id: it keys
 * the table layout (ui.UiWidget), not the data, so all areas share one layout.
 *
 * TM-1000002058 G3: the FK columns („Benachrichtigungstyp“) show their texts (`resolve_fkey_texts`, as the bm
 * config page does), and every row has an edit button (`editOption = 'button'`) - the table has no row-click
 * selection, so edit / activate was reachable by right-click only. The edit form takes the same READ overwrite of
 * the area as the create form (mvs-widget-table.handleEdit reuses onFunctionCreateNew).
 *
 * @ticket TM-1000002058
 */
@Component({
    selector: 'bm-billing-area-premium-change-notice-types',
    templateUrl: 'bm-billing-area-premium-change-notice-types.component.html',
    styleUrls: ['bm-billing-area-premium-change-notice-types.component.scss'],
    standalone: false
})
export class BmBillingAreaPremiumChangeNoticeTypesComponent implements OnInit, OnChanges {

    /** Configuration widget alias (requirement 01 §6.2). */
    static readonly WIDGET_ALIAS: string = 'config.bm.PremiumChangeNoticeType.area';

    /** TM-1000002058 G3: the rule of the section, as one neutral line above the table. */
    static readonly INFO_TEXT: string = 'Je Abrechnungsbereich darf genau eine Konfiguration aktiv sein; ohne aktive '
        + 'Konfiguration werden für diesen Bereich keine Beitragsänderungen ermittelt.';

    protected readonly infoText: string = BmBillingAreaPremiumChangeNoticeTypesComponent.INFO_TEXT;

    initialized: boolean = false;

    /** The billing area whose configurations are listed - fixed filter and FK default of a new row. */
    @Input() billingAreaId: number;

    premiumChangeNoticeTypesWidgetData: WidgetData;

    /** The billing area as import object context of the create form (backend FK defaulting). */
    importObjectContext: DtoImportObjectContext;

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Builds the table for the bound billing area.
     */
    refreshComponent() {
        // PremiumChangeNoticeType.billingArea is the FK (DB column billing_area_id). QL filter form: e.billingAreaId.
        const filterCriteria = FilterCriteria.create(
            'e.billingAreaId',
            FilterCriteria.cOperatorEqual,
            this.billingAreaId
        );

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: bm.PremiumChangeNoticeType configuration table of ONE billing area - which ns notification
         *   type sends the premium change letter („Beitragsänderungs-Information“) of the area's billing runs;
         *   metadata-driven create/edit with the area as fixed filter and read-only FK default.
         */
        this.premiumChangeNoticeTypesWidgetData = WidgetFactory.createWidgetTableEntityBasicQl(
            BmBillingAreaPremiumChangeNoticeTypesComponent.WIDGET_ALIAS,
            'Beitragsänderungs-Information',
            'bm.PremiumChangeNoticeType',
            [filterCriteria],
            [],
            // TM-1000002058 G3: FK texts instead of bare ids (column „Benachrichtigungstyp“)
            true,
            // TM-1000002058 G3: a per-row edit button - the only left-click way to edit / activate a row
            WidgetDataParam.create('editOption', 'button')
        );

        // CONFIG-012: the area is pre-filled AND read-only - a configuration can never be moved to another area here.
        const fields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'billingAreaDtoId', fieldValue: this.billingAreaId}
        ];
        this.premiumChangeNoticeTypesWidgetData.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(fields);

        this.importObjectContext = new DtoImportObjectContext([
            new DtoImportObjectContextEntry('bm.BillingArea', this.billingAreaId)
        ]);

        this.initialized = true;
    }

    /**
     * Process changes within Binding: another billing area rebuilds the table.
     * @param changes
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['billingAreaId']) {
            this.refreshComponent();
        }
    }
}
